import React from 'react';
import {
  DialogTitle,
  Divider,
  Typography,
  Box,
  Button,
} from '@mui/material';
import AlertDialog from '../AlertDialog';
import PayYourLoan from '../../feature/payYourLoan';
import { Currency, ProductType } from '../../types/account';
import { parseAmountWithCurrency, parseCurrencyAbbreviation } from '../../utils/helpers';
import analytics from '../../utils/analytics';
import { productTypeLabels } from '../CreditOverview';

interface PendingPaymentDialogInterface{
  hasDelinquency: boolean,
  unpaidDelinquencyAmount: number,
  unpaidInstallmentAmount: number,
  unpaidCollectionFeeAmount: number,
  paymentMethod: string,
  token: string,
  loanId: string,
  paidAmount: number,
  productType: ProductType,
  unpaidTotalAmount: number,
  unpaidAmount: number,
  currency: Currency,
  partnerOrStoreLabel: string,
  }

function PendingPaymentDialog(props:PendingPaymentDialogInterface) {
  const {
    paymentMethod,
    hasDelinquency,
    unpaidDelinquencyAmount,
    unpaidInstallmentAmount,
    unpaidCollectionFeeAmount,
    token,
    loanId,
    paidAmount,
    productType,
    unpaidTotalAmount,
    unpaidAmount,
    currency,
    partnerOrStoreLabel,
  } = props;

  const [isTriggered, setIsTriggered] = React.useState(false);

  const totalToDeposit = unpaidInstallmentAmount + unpaidDelinquencyAmount
    + unpaidCollectionFeeAmount;
  const isDPPDelinquent = hasDelinquency && paymentMethod !== 'dpp';

  React.useEffect(() => {
    if (isDPPDelinquent) {
      setIsTriggered(true);
    }
  }, []);

  const parseAmountWitoutCurrencyAbbreviation = (amount: number) => (
    parseAmountWithCurrency(currency, amount, true, false, currency.precision)
  );

  const maxAmountLength = Math.max(
    parseAmountWitoutCurrencyAbbreviation(totalToDeposit).length
    - 2,
    2,
  );

  const handlePayMortgage = () => {
    analytics.track('Click Boton', {
      text: 'Pagar crédito',
      initiator: 'usuario',
    });
    window.location.href = process.env.REACT_APP_ANDES_PAYMENT_URL || '';
  };

  return (
    <AlertDialog
      pageCall={{
        category: 'PU - LOGIN',
        name: 'DEUDA PENDIENTE',
      }}
      maxWidth="sm"
      hideCloseButton="corner"
      isTriggered={isTriggered}
      dialogTitle={(!hasDelinquency) && (
        <DialogTitle
          style={{ textAlign: 'left' }}
        >
          Datos para la transferencia
        </DialogTitle>
      )}
      hideSubmitButton
      dialogContent={(
        <Box sx={{ padding: 2, paddingBottom: 0 }}>
          <Typography
            variant="h6"
            fontWeight="bold"
            align="center"
            gutterBottom
          >
            !Atención!
            Tienes una deuda pendiente
          </Typography>
          <Typography
            variant="body1"
            color="secondary"
            fontWeight="bold"
            align="center"
            sx={{ paddingInline: 3, marginBottom: 2.5 }}
          >
            {' '}
            (
            {productTypeLabels[productType]}
            {' '}
            -
            {' '}
            {partnerOrStoreLabel}
            )
          </Typography>
          <Typography
            variant="body2"
            align="center"
            gutterBottom
            sx={{ paddingInline: 3 }}
          >
            Ponte al día y evita seguir acumulando intereses por morosidad.
          </Typography>
          <br />
          <div style={{ display: 'flex', placeContent: 'space-between', paddingInline: '10px' }}>
            <Typography
              variant="body2"
              align="center"
              gutterBottom
              color="text.secondary"
              display="inline"
            >
              Monto de cuotas pendientes
            </Typography>
            <Box sx={{
              textAlign: 'right',
              display: 'flex',
              width: 'fit-content',
            }}
            >
              <Typography
                width="fit-content"
                variant="body2"
                fontWeight="bold"
                color="secondary"
              >
                +
                {' '}
                {parseCurrencyAbbreviation(currency)}
                {' '}
              </Typography>
              <Typography width={`${maxAmountLength - 1}em`} color="secondary" variant="body2" fontWeight="bold">
                {parseAmountWitoutCurrencyAbbreviation(unpaidInstallmentAmount)}
              </Typography>
            </Box>
          </div>
          <div style={{ display: 'flex', placeContent: 'space-between', paddingInline: '10px' }}>
            <Typography
              variant="body2"
              align="center"
              gutterBottom
              color="text.secondary"
              display="inline"
            >
              Intereses por morosidad
            </Typography>
            <Box sx={{
              textAlign: 'right',
              display: 'flex',
              width: 'fit-content',
            }}
            >
              <Typography
                width="fit-content"
                variant="body2"
                fontWeight="bold"
                color="secondary"
              >
                +
                {' '}
                {parseCurrencyAbbreviation(currency)}
                {' '}
              </Typography>
              <Typography width={`${maxAmountLength - 1}em`} color="secondary" variant="body2" fontWeight="bold">
                {parseAmountWitoutCurrencyAbbreviation(unpaidDelinquencyAmount)}
              </Typography>
            </Box>
          </div>
          <div style={{ display: 'flex', placeContent: 'space-between', paddingInline: '10px' }}>
            <Typography
              variant="body2"
              align="center"
              gutterBottom
              color="text.secondary"
              display="inline"
            >
              Gastos de cobranza
            </Typography>
            <Box sx={{
              textAlign: 'right',
              display: 'flex',
              width: 'fit-content',
            }}
            >
              <Typography width="fit-content" variant="body2" fontWeight="bold" color="secondary">
                +
                {' '}
                {parseCurrencyAbbreviation(currency)}
                {' '}
              </Typography>
              <Typography width={`${maxAmountLength - 1}em`} color="secondary" variant="body2" fontWeight="bold">
                {parseAmountWitoutCurrencyAbbreviation(unpaidCollectionFeeAmount)}
              </Typography>
            </Box>
          </div>
          <Divider />
          <div style={{
            display: 'flex', placeContent: 'space-between', paddingInline: '10px', marginTop: '7.5px',
          }}
          >
            <Typography
              variant="body2"
              align="center"
              gutterBottom
              fontWeight="bold"
              color="text.secondary"
              display="inline"
            >
              Total
            </Typography>
            <Box sx={{
              textAlign: 'right',
              display: 'flex',
              width: 'fit-content',
            }}
            >
              <Typography width="fit-content" variant="body2" fontWeight="bold" color="secondary">
                {parseCurrencyAbbreviation(currency)}
              </Typography>
              <Typography width={`${maxAmountLength - 1}em`} color="secondary" variant="body2" fontWeight="bold">
                {parseAmountWitoutCurrencyAbbreviation(totalToDeposit)}
              </Typography>
            </Box>
          </div>
          <Box sx={{
            width: '100%', display: 'flex', justifyContent: 'center', marginTop: 10,
          }}
          >
            {productType !== 'mortgage' ? (
              <PayYourLoan
                pageCategory="PU - LOGIN"
                unpaidAmount={unpaidAmount}
                paidAmount={paidAmount}
                productType={productType}
                unpaidTotalAmount={unpaidTotalAmount}
                token={token}
                loanId={loanId}
                loanCurrency={currency}
              />
            ) : <Button variant="contained" color="primary" onClick={handlePayMortgage}>Pagar crédito</Button>}
          </Box>
        </Box>
      )}
    />
  );
}

export default PendingPaymentDialog;
